import { FormattedMessage, FormattedNumber, useIntl } from 'react-intl'

import { cn } from '@gto/ui/cn'
import type { ColumnDef } from '@gto/ui/data-table'
import { createColumns } from '@gto/ui/data-table'
import { Typography } from '@gto/ui/typography'

import type { AffiliateAdminPartnerListSchema } from '@/api/gen'
import { AccountIndicator } from '@/components/account-indicator'
import { PartnerCell } from '@/components/partner-cell'
import { StateDot } from '@/components/state-dot'
import { StatusBadge } from '@/components/status-badge'
import { fluidColumn, headerWithHint } from '@/components/table-columns'
import { TierTag } from '@/components/tier-tag'
import { countryFlagEmoji } from '@/utils/country-flag'
import { toAmount } from '@/utils/to-amount'

/**
 * Whether a numeric cell should read as muted (zero is meaningful — "this
 * partner has not produced anything yet" — but visually noisy if it gets
 * full-strength weight).
 */
const isZero = (n: number | null | undefined): boolean => n == null || n === 0

/**
 * Sorting is left ENABLED (the TanStack default) ONLY on columns whose `id` matches one
 * of the BE `valid_sort_fields` keys (see `partners-search-params.ts` and gto-wizard
 * `app/account/affiliates/api/v4/admin.py`); every other column sets
 * `enableSorting: false`. The page maps the column id back to the URL `sort` param, so
 * the ids MUST line up with the URL enum.
 *
 * These are `id` (display) columns, not `accessorKey`s: the ids are BE sort keys, not
 * row fields, and sorting is server-side so no local accessor is needed.
 */
export const usePartnerColumns = (): ColumnDef<AffiliateAdminPartnerListSchema>[] => {
  const { formatMessage } = useIntl()

  return createColumns<AffiliateAdminPartnerListSchema>([
    {
      id: 'country',
      header: formatMessage({ id: 'admin.partners.country' }),
      ...fluidColumn({ min: 80, flex: 0.5 }),
      enableSorting: false,
      cell: ({ row }) => {
        const code = row.original.country
        const flag = countryFlagEmoji(code)
        if (flag === '') {
          // Unknown country: empty cell. No em-dash, no placeholder — the
          // marketing-team aesthetic is "blank when blank".
          return null
        }

        return (
          <span className="inline-flex items-center gap-3">
            <span
              aria-hidden="true"
              className="leading-none text-base"
            >
              {flag}
            </span>
            <Typography
              color="text.secondary"
              variant="caption"
            >
              {code.toUpperCase()}
            </Typography>
          </span>
        )
      },
    },
    {
      id: 'account',
      header: formatMessage({ id: 'admin.partners.account' }),
      ...fluidColumn({ min: 90, flex: 0.4 }),
      enableSorting: false,
      cell: ({ row }) => <AccountIndicator isPaired={row.original.is_paired} />,
    },
    {
      id: 'beta',
      header: formatMessage({ id: 'admin.partners.beta' }),
      ...fluidColumn({ min: 100, flex: 0.4 }),
      enableSorting: false,
      cell: ({ row }) =>
        // The field ships with gto-wizard#2394 — until that BE deploys, rows
        // arrive without it; blank cell beats a made-up red dot.
        typeof row.original.can_access_portal === 'boolean' ? (
          <StateDot
            offLabelId="admin.partners.beta.off"
            offTooltipId="admin.partners.beta.off.tooltip"
            on={row.original.can_access_portal}
            onLabelId="admin.partners.beta.on"
            onTooltipId="admin.partners.beta.on.tooltip"
          />
        ) : null,
    },
    {
      // `fully_migrated` is the admin-confirmed reconciliation flag, distinct
      // from `is_migrated` (legacy-origin). Renders as a state dot so the
      // column reads at a glance alongside Account and Beta. Defensive against
      // a transient BE shape where the field is missing — blank beats a made-up
      // value — same pattern as the `beta` column.
      id: 'fullyMigrated',
      header: formatMessage({ id: 'admin.partners.fullyMigrated' }),
      ...fluidColumn({ min: 110, flex: 0.45 }),
      enableSorting: false,
      cell: ({ row }) =>
        typeof row.original.fully_migrated === 'boolean' ? (
          <StateDot
            offLabelId="admin.partners.fullyMigrated.off"
            offTooltipId="admin.partners.fullyMigrated.off.tooltip"
            on={row.original.fully_migrated}
            onLabelId="admin.partners.fullyMigrated.on"
            onTooltipId="admin.partners.fullyMigrated.on.tooltip"
          />
        ) : null,
    },
    {
      id: 'name',
      header: formatMessage({ id: 'admin.partners.name' }),
      ...fluidColumn({ min: 220, flex: 1.6, stacked: true }),
      cell: ({ row }) => (
        <PartnerCell
          displayName={row.original.display_name}
          email={row.original.email}
        />
      ),
    },
    {
      id: 'tier',
      header: formatMessage({ id: 'admin.partners.tier' }),
      ...fluidColumn({ min: 120, flex: 0.8 }),
      enableSorting: false,
      cell: ({ row }) =>
        row.original.tier == null ? null : (
          <TierTag
            commissionRate={row.original.tier.commission_rate}
            tierName={row.original.tier.name}
          />
        ),
    },
    {
      id: 'orders',
      header: formatMessage({ id: 'admin.partners.orders' }),
      ...fluidColumn({ min: 80, flex: 0.5, align: 'right' }),
      cell: ({ row }) => (
        <Typography
          className={cn(isZero(row.original.total_orders) && 'text-text-disabled')}
          variant="body2"
        >
          <FormattedNumber value={row.original.total_orders ?? 0} />
        </Typography>
      ),
    },
    {
      id: 'customers',
      header: formatMessage({ id: 'admin.partners.customers' }),
      ...fluidColumn({ min: 90, flex: 0.5, align: 'right' }),
      cell: ({ row }) => (
        <Typography
          className={cn(isZero(row.original.unique_customers) && 'text-text-disabled')}
          variant="body2"
        >
          <FormattedNumber value={row.original.unique_customers ?? 0} />
        </Typography>
      ),
    },
    {
      id: 'last_order',
      header: formatMessage({ id: 'admin.partners.lastOrder' }),
      ...fluidColumn({ min: 110, flex: 0.7, align: 'right' }),
      cell: ({ row }) => {
        const lastOrderDate = row.original.last_order_date
        if (lastOrderDate == null) {
          return (
            <Typography
              color="text.disabled"
              variant="body2"
            >
              <FormattedMessage id="admin.partners.lastOrder.never" />
            </Typography>
          )
        }
        const d = new Date(lastOrderDate)

        return (
          <Typography
            color="text.secondary"
            variant="body2"
          >
            {d.toLocaleDateString('en-US', {
              month: 'short',
              day: 'numeric',
              year: 'numeric',
            })}
          </Typography>
        )
      },
    },
    {
      id: 'revenue',
      header: formatMessage({ id: 'admin.partners.revenue' }),
      ...fluidColumn({ min: 110, flex: 0.8, align: 'right' }),
      cell: ({ row }) => {
        const amount = toAmount(row.original.total_revenue, 'partners-table total_revenue:')

        return (
          <Typography
            className={cn('font-medium', isZero(amount) && 'font-normal text-text-disabled')}
            variant="body2"
          >
            <FormattedNumber
              currency="USD"
              maximumFractionDigits={0}
              style="currency"
              value={amount}
            />
          </Typography>
        )
      },
    },
    {
      id: 'commissions',
      header: formatMessage({ id: 'admin.partners.commissions' }),
      ...fluidColumn({ min: 120, flex: 0.8, align: 'right' }),
      cell: ({ row }) => {
        const amount = toAmount(row.original.total_commissions, 'partners-table total_commissions:')

        // Brand-green accent only when the partner has actually earned something
        // — restraint, per the spec: zero commissions read as muted, not as a
        // bright "this is the metric" green.
        return (
          <Typography
            variant="body2"
            className={cn(
              'font-medium',
              amount > 0 ? 'text-primary-main' : 'font-normal text-text-disabled',
            )}
          >
            <FormattedNumber
              currency="USD"
              maximumFractionDigits={0}
              style="currency"
              value={amount}
            />
          </Typography>
        )
      },
    },
    {
      // CURRENT withdrawable balance from `balance_available_usd` — distinct from
      // `total_commissions` (lifetime gross) and `pending_payouts_usd` (already in
      // an open request). Not sortable: the BE `valid_sort_fields` enum (see the
      // sorting-constraint comment above) does not include this field, and the
      // value is computed per-row from unlocked commissions rather than a column
      // index. The header carries the "Available to withdraw now" hint the brief asked for.
      id: 'balance',
      header: headerWithHint(
        formatMessage({ id: 'admin.partners.balance' }),
        formatMessage({ id: 'admin.partners.balance.tooltip' }),
      ),
      ...fluidColumn({ min: 120, flex: 0.8, align: 'right' }),
      enableSorting: false,
      cell: ({ row }) => {
        const amount = toAmount(
          row.original.balance_available_usd,
          'partners-table balance_available_usd:',
        )

        return (
          <Typography
            className={cn('font-medium', isZero(amount) && 'font-normal text-text-disabled')}
            variant="body2"
          >
            <FormattedNumber
              currency="USD"
              maximumFractionDigits={0}
              style="currency"
              value={amount}
            />
          </Typography>
        )
      },
    },
    {
      id: 'status',
      header: formatMessage({ id: 'admin.partners.status' }),
      ...fluidColumn({ min: 100, flex: 0.6, align: 'right' }),
      enableSorting: false,
      cell: ({ row }) => <StatusBadge status={row.original.status} />,
    },
  ])
}
